import type { TabsProps } from '@risk-smart/themed-cloudscape-components/tabs';
import type { GetIndicatorByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useTranslation } from 'react-i18next';
import EntityNotificationHistoryTab from 'src/components/entity-notification-history/EntityNotificationHistoryTab';
import LinkedItemsTab from 'src/pages/linked-items/LinkedItemsTab';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';
import { filterEmptyTabs } from '@/utils/tabUtils';

import DetailsTab from './tabs/details/Tab';
import ResultsTab from './tabs/results/Tab';

export const useTabs = (
  detailsPath: string,
  indicator?: GetIndicatorByIdQuery['indicator'][0]
) => {
  const { t, i18n } = useTranslation(['common']);
  const {
    hasPermission: canViewLinkedItems,
    loading: canViewLinkedItemsLoading,
  } = useHasPermissionQuery('read:linked_item', indicator);
  const notificationsEnabled = useIsFeatureVisibleToOrg('notifications');

  const tabs: TabsProps.Tab[] = [
    {
      label: t('details'),
      id: 'details',
      content: indicator && <DetailsTab indicator={indicator} />,
      href: detailsPath,
    },
    {
      label: i18n.format(t('indicator_result_other'), 'capitalize'),
      id: 'results',
      content: indicator && (
        <ResultsTab indicatorType={indicator.Type} parent={indicator} />
      ),
      href: `${detailsPath}/results`,
    },
  ];

  if (canViewLinkedItems && !canViewLinkedItemsLoading) {
    tabs.push({
      label: t('linkedItems.tab_title'),
      id: 'linkedItems',
      content: indicator && (
        <LinkedItemsTab
          parent={indicator}
          parentType={Parent_Type_Enum.Indicator}
        />
      ),
      href: `${detailsPath}/linked-items`,
    });
  }

  if (notificationsEnabled && indicator) {
    tabs.push({
      label: t('notificationHistory.tab_title'),
      id: 'notificationHistory',
      content: <EntityNotificationHistoryTab objectId={indicator.Id} />,
      href: `${detailsPath}/notification-history`,
    });
  }

  return filterEmptyTabs(tabs);
};
